/**
 * /growth — Growth Journey page.
 *
 * Two tabs: MoM (month-on-month, operational pulse) and YoY (year-on-year,
 * strategic). Each renders three sections: brand · city · outlet.
 *
 * Single data fetch (trailing 14 months) feeds both tabs — switching is
 * instant, no requery.
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown, Minus, Sparkles, Activity, Bike } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { formatINRCompact } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useOutlets } from '@/hooks/useOutlets';
import type { Outlet } from '@/types/domain';
import { useGrowthData, type OutletMonth } from './hooks';
import { EventsView } from './EventsView';
import { useChannelSplit } from '@/hooks/useChannelSplit';
import { DeliveryDineinSplit } from '@/components/charts/DeliveryDineinSplit';

type Tab = 'mom' | 'yoy' | 'discount' | 'ads' | 'events';
type YoyPeriod = 'last_month' | 'last_quarter' | 'ytd';

export function GrowthJourney() {
  const [tab, setTab] = useState<Tab>('mom');
  const [yoyPeriod, setYoyPeriod] = useState<YoyPeriod>('last_month');
  const { months, byOutletMonth, metaAdsByMonth, isLoading } = useGrowthData();
  const { data: outlets } = useOutlets();

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <SectionHeader
          title={
            <span className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-500" />
              Growth Journey
              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-2xs font-medium text-emerald-700 dark:text-emerald-300">
                <Sparkles className="h-3 w-3" /> live
              </span>
            </span>
          }
          description="Where Flax is growing — and where it isn't. Brand, city, and outlet, both month-over-month and year-over-year."
        />
        <TabSwitcher tab={tab} setTab={setTab} />
      </div>

      {isLoading || !outlets ? (
        <LoadingState />
      ) : tab === 'mom' ? (
        <MoMView months={months} byOutletMonth={byOutletMonth} outlets={outlets} />
      ) : tab === 'yoy' ? (
        <YoYView
          months={months}
          byOutletMonth={byOutletMonth}
          outlets={outlets}
          period={yoyPeriod}
          setPeriod={setYoyPeriod}
        />
      ) : tab === 'discount' ? (
        <SpendVsSalesView
          kind="discount"
          months={months}
          byOutletMonth={byOutletMonth}
          metaAdsByMonth={metaAdsByMonth}
          outlets={outlets}
        />
      ) : tab === 'ads' ? (
        <SpendVsSalesView
          kind="ads"
          months={months}
          byOutletMonth={byOutletMonth}
          metaAdsByMonth={metaAdsByMonth}
          outlets={outlets}
        />
      ) : (
        <EventsView />
      )}
    </div>
  );
}

// ───────── Tab switcher ─────────

function TabSwitcher({ tab, setTab }: { tab: Tab; setTab: (t: Tab) => void }) {
  const tabs: Array<{ k: Tab; label: string }> = [
    { k: 'mom', label: 'MoM' },
    { k: 'yoy', label: 'YoY' },
    { k: 'discount', label: 'Discount vs sales' },
    { k: 'ads', label: 'Ads vs sales' },
    { k: 'events', label: 'Events & festivals' },
  ];
  return (
    <div className="inline-flex flex-wrap rounded-md border border-border/60 bg-card p-0.5 text-xs">
      {tabs.map((t) => (
        <button
          key={t.k}
          onClick={() => setTab(t.k)}
          className={cn(
            'rounded px-3 py-1.5 font-medium transition',
            tab === t.k ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ───────── Helpers ─────────

function sumByMonth(byOutletMonth: OutletMonth[], month: string, outletIds?: Set<string>): number {
  let s = 0;
  for (const r of byOutletMonth) {
    if (r.month !== month) continue;
    if (outletIds && !outletIds.has(r.outletId)) continue;
    s += r.netSales;
  }
  return s;
}

function pretty(month: string | undefined): string {
  if (!month) return '—';
  // YYYY-MM → "Jun 25"
  const parts = month.split('-').map(Number);
  const y = parts[0] ?? 2000;
  const m = parts[1] ?? 1;
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
}

function growthTone(pct: number | null): 'good' | 'warn' | 'bad' | 'na' {
  if (pct === null || !Number.isFinite(pct)) return 'na';
  if (pct >= 5) return 'good';
  if (pct >= -3) return 'warn';
  return 'bad';
}

function growthClasses(tone: 'good' | 'warn' | 'bad' | 'na'): string {
  return tone === 'good'
    ? 'text-emerald-600 dark:text-emerald-400'
    : tone === 'bad'
      ? 'text-rose-600 dark:text-rose-400'
      : tone === 'warn'
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-muted-foreground';
}

function ArrowIcon({ tone }: { tone: 'good' | 'warn' | 'bad' | 'na' }) {
  if (tone === 'good') return <TrendingUp className="h-3.5 w-3.5" />;
  if (tone === 'bad') return <TrendingDown className="h-3.5 w-3.5" />;
  return <Minus className="h-3.5 w-3.5" />;
}

// ───────── MoM view ─────────

function MoMView({
  months,
  byOutletMonth,
  outlets,
}: {
  months: string[];
  byOutletMonth: OutletMonth[];
  outlets: Outlet[];
}) {
  // Brand level — last closed month vs previous month (skip current MTD,
  // it's a partial month and would always look like a regression).
  const latestClosed = months[months.length - 2] ?? '';
  const previousClosed = months[months.length - 3] ?? '';
  const brandLatest = sumByMonth(byOutletMonth, latestClosed);
  const brandPrev = sumByMonth(byOutletMonth, previousClosed);
  const brandPct = brandPrev > 0 ? ((brandLatest - brandPrev) / brandPrev) * 100 : null;

  // 13-month line of brand-level totals
  const brandLine = months.slice(0, -1).map((m) => ({ month: m, value: sumByMonth(byOutletMonth, m) }));

  // City rollup
  const byCity = new Map<string, Outlet[]>();
  for (const o of outlets) {
    const city = o.city || 'Unknown';
    if (!byCity.has(city)) byCity.set(city, []);
    byCity.get(city)!.push(o);
  }
  const cityRows = Array.from(byCity.entries())
    .map(([city, cityOutlets]) => {
      const ids = new Set(cityOutlets.map((o) => o.id));
      const latest = sumByMonth(byOutletMonth, latestClosed, ids);
      const prev = sumByMonth(byOutletMonth, previousClosed, ids);
      const pct = prev > 0 ? ((latest - prev) / prev) * 100 : null;
      return { city, latest, prev, pct, outletCount: cityOutlets.length };
    })
    .sort((a, b) => b.latest - a.latest);

  // Trailing 6 closed months for the matrix columns — gives the operator a
  // mini time series per outlet without needing to read the sparkline.
  const trailingMonths = months.slice(0, -1).slice(-6);

  // Outlet rows
  const outletRows = outlets
    .map((o) => {
      const ids = new Set([o.id]);
      const latest = sumByMonth(byOutletMonth, latestClosed, ids);
      const prev = sumByMonth(byOutletMonth, previousClosed, ids);
      const pct = prev > 0 ? ((latest - prev) / prev) * 100 : null;
      const isNew = prev === 0 && latest > 0;
      const trailing = trailingMonths.map((m) => sumByMonth(byOutletMonth, m, ids));
      const line = months.slice(0, -1).map((m) => sumByMonth(byOutletMonth, m, ids));
      return { outlet: o, latest, prev, pct, isNew, line, trailing };
    })
    .filter((r) => r.latest > 0 || r.prev > 0)
    .sort((a, b) => {
      if (a.isNew && !b.isNew) return -1;
      if (!a.isNew && b.isNew) return 1;
      const aPct = a.pct ?? -999;
      const bPct = b.pct ?? -999;
      return bPct - aPct;
    });

  // Year-vs-year overlay — for each calendar month-of-year, sum this-year
  // and last-year buckets if we have them. Index 0 = Jan, 11 = Dec.
  const latestY = Number(latestClosed.slice(0, 4));
  const yvyData: Array<{ monthIdx: number; thisY: number | null; lastY: number | null }> = [];
  for (let m = 0; m < 12; m++) {
    const thisKey = `${latestY}-${String(m + 1).padStart(2, '0')}`;
    const lastKey = `${latestY - 1}-${String(m + 1).padStart(2, '0')}`;
    const thisHas = months.includes(thisKey);
    const lastHas = months.includes(lastKey);
    yvyData.push({
      monthIdx: m,
      thisY: thisHas ? sumByMonth(byOutletMonth, thisKey) : null,
      lastY: lastHas ? sumByMonth(byOutletMonth, lastKey) : null,
    });
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-4">
      <BrandHeroMoM
        latestMonth={latestClosed}
        prevMonth={previousClosed}
        latest={brandLatest}
        prev={brandPrev}
        pct={brandPct}
        line={brandLine}
      />

      <MoMChannelSplitCard />

      <YearVsYearBrand thisYear={latestY} data={yvyData} />

      <Section title="Cities">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {cityRows.map((c) => (
            <CityCard key={c.city} {...c} latestMonth={latestClosed} prevMonth={previousClosed} />
          ))}
        </div>
      </Section>

      <Section title="Outlets" trailing={<span className="text-2xs text-muted-foreground">Sorted by MoM growth · new outlets pinned top</span>}>
        <OutletMatrix
          rows={outletRows}
          months={months.slice(0, -1)}
          latestLabel={pretty(latestClosed)}
          prevLabel={pretty(previousClosed)}
          trailingMonths={trailingMonths}
        />
      </Section>
    </motion.div>
  );
}

// ───────── Channel split (last month vs prior) ─────────

function MoMChannelSplitCard() {
  const split = useChannelSplit({ outletId: 'all', period: 'last_month' });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bike className="h-4 w-4 text-muted-foreground" /> Delivery vs Dine-in
          <span className="ml-1 text-2xs font-normal text-muted-foreground">
            last full month · vs month before
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <DeliveryDineinSplit
          delivery={split.delivery}
          dineIn={split.dineIn}
          pickUp={split.pickUp}
          totalSales={split.totalSales}
          loading={split.isLoading}
          compact
        />
      </CardContent>
    </Card>
  );
}

// ───────── Year-vs-year brand overlay ─────────

function YearVsYearBrand({
  thisYear,
  data,
}: {
  thisYear: number;
  data: Array<{ monthIdx: number; thisY: number | null; lastY: number | null }>;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const w = 720;
  const h = 180;
  const padL = 36;
  const padR = 12;
  const padT = 14;
  const padB = 24;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const allVals = data.flatMap((d) => [d.thisY, d.lastY]).filter((v): v is number => v != null && v > 0);
  const max = allVals.length > 0 ? Math.max(...allVals) : 1;
  const step = innerW / 11;
  const px = (i: number) => padL + i * step;
  const py = (v: number) => padT + innerH - (v / max) * innerH;

  const pointsFor = (key: 'thisY' | 'lastY'): string =>
    data
      .map((d, i) => (d[key] != null ? `${px(i)},${py(d[key]!)}` : null))
      .filter(Boolean)
      .join(' ');

  const MONTH_LABELS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
  const hovered = hover !== null ? data[hover] : null;
  const hoverYoY = (() => {
    if (!hovered) return null;
    if (hovered.thisY == null || hovered.lastY == null || hovered.lastY <= 0) return null;
    return ((hovered.thisY - hovered.lastY) / hovered.lastY) * 100;
  })();

  return (
    <Card className="overflow-hidden border-border/60">
      <CardContent className="space-y-3 p-4 md:p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <div className="text-2xs uppercase tracking-wide text-muted-foreground">Brand sales · {thisYear} vs {thisYear - 1}</div>
            <div className="text-sm text-muted-foreground">
              How this year's growth shape compares to last year's.
            </div>
          </div>
          <div className="flex items-center gap-3 text-2xs">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-3 rounded-sm bg-emerald-500" /> {thisYear}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-3 rounded-sm bg-muted-foreground/50" /> {thisYear - 1}
            </span>
          </div>
        </div>
        <div className="relative">
          <svg
            width="100%"
            viewBox={`0 0 ${w} ${h}`}
            className="block"
            onMouseLeave={() => setHover(null)}
            onMouseMove={(e) => {
              const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
              const x = ((e.clientX - rect.left) / rect.width) * w;
              const idx = Math.max(0, Math.min(11, Math.round((x - padL) / step)));
              setHover(idx);
            }}
          >
            {/* Y-axis dashed gridlines at 25/50/75/100% of max */}
            {[0.25, 0.5, 0.75, 1].map((f) => (
              <line
                key={f}
                x1={padL}
                x2={padL + innerW}
                y1={padT + innerH - innerH * f}
                y2={padT + innerH - innerH * f}
                stroke="currentColor"
                strokeOpacity="0.08"
                strokeDasharray="2 3"
              />
            ))}
            {/* Last year line — soft, behind */}
            <polyline
              points={pointsFor('lastY')}
              fill="none"
              stroke="hsl(0 0% 50%)"
              strokeOpacity="0.55"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* This year line — emerald, in front */}
            <polyline
              points={pointsFor('thisY')}
              fill="none"
              stroke="hsl(160 84% 39%)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* Points */}
            {data.map((d, i) => (
              <g key={i}>
                {d.lastY != null && (
                  <circle cx={px(i)} cy={py(d.lastY)} r={hover === i ? 3.5 : 2} fill="hsl(0 0% 50%)" style={{ transition: 'r 120ms ease' }} />
                )}
                {d.thisY != null && (
                  <circle cx={px(i)} cy={py(d.thisY)} r={hover === i ? 4 : 2.5} fill="hsl(160 84% 39%)" style={{ transition: 'r 120ms ease' }} />
                )}
              </g>
            ))}
            {/* X-axis month labels */}
            {MONTH_LABELS.map((l, i) => (
              <text key={i} x={px(i)} y={h - 6} textAnchor="middle" className="fill-muted-foreground text-[10px]">
                {l}
              </text>
            ))}
            {/* Hover vertical guide */}
            {hover !== null && (
              <line
                x1={px(hover)}
                x2={px(hover)}
                y1={padT}
                y2={padT + innerH}
                stroke="currentColor"
                strokeOpacity="0.2"
                strokeDasharray="2 3"
              />
            )}
          </svg>
          {hover !== null && hovered && (
            <div
              className="pointer-events-none absolute z-10 -translate-x-1/2 rounded-md border border-border/60 bg-card px-2.5 py-1.5 text-2xs shadow-md"
              style={{
                left: `${(px(hover) / w) * 100}%`,
                top: `${(padT / h) * 100}%`,
              }}
            >
              <div className="font-medium text-muted-foreground">
                {new Date(2000, hovered.monthIdx, 1).toLocaleDateString('en-IN', { month: 'long' })}
              </div>
              <div className="mt-0.5 flex items-center gap-2 tabular-nums">
                <span className="inline-block h-2 w-2 rounded-sm bg-emerald-500" />
                {hovered.thisY != null ? formatINRCompact(hovered.thisY) : '—'}
                <span className="text-muted-foreground">{thisYear}</span>
              </div>
              <div className="mt-0.5 flex items-center gap-2 tabular-nums">
                <span className="inline-block h-2 w-2 rounded-sm bg-muted-foreground/50" />
                {hovered.lastY != null ? formatINRCompact(hovered.lastY) : '—'}
                <span className="text-muted-foreground">{thisYear - 1}</span>
              </div>
              {hoverYoY != null && (
                <div className={cn('mt-1 font-semibold tabular-nums', growthClasses(growthTone(hoverYoY)))}>
                  {hoverYoY >= 0 ? '+' : ''}
                  {hoverYoY.toFixed(1)}% YoY
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ───────── YoY view ─────────

function YoYView({
  months,
  byOutletMonth,
  outlets,
  period,
  setPeriod,
}: {
  months: string[];
  byOutletMonth: OutletMonth[];
  outlets: Outlet[];
  period: YoyPeriod;
  setPeriod: (p: YoyPeriod) => void;
}) {
  // Pick the comparison window based on the period selector.
  // last_month: latest closed month vs the same month last year
  // last_quarter: latest closed quarter vs LY same quarter
  // ytd: months 0..(current-1) of this year vs same months LY
  // (We only have 13 months of data, so YTD coverage is partial early in the year.)
  const today = new Date();
  const todayY = today.getFullYear();
  const todayM = today.getMonth(); // 0-11
  const lastClosedY = todayM === 0 ? todayY - 1 : todayY;
  const lastClosedM = todayM === 0 ? 11 : todayM - 1;

  const fmt = (y: number, m: number) => `${y}-${String(m + 1).padStart(2, '0')}`;
  let thisMonths: string[] = [];
  let lyMonths: string[] = [];
  if (period === 'last_month') {
    thisMonths = [fmt(lastClosedY, lastClosedM)];
    lyMonths = [fmt(lastClosedY - 1, lastClosedM)];
  } else if (period === 'last_quarter') {
    const qStart = lastClosedM - (lastClosedM % 3);
    for (let i = 0; i < 3; i++) {
      thisMonths.push(fmt(lastClosedY, qStart + i));
      lyMonths.push(fmt(lastClosedY - 1, qStart + i));
    }
  } else {
    // YTD: Jan..lastClosedM
    for (let i = 0; i <= lastClosedM; i++) {
      thisMonths.push(fmt(lastClosedY, i));
      lyMonths.push(fmt(lastClosedY - 1, i));
    }
  }

  // Filter to months we actually have data for
  const knownMonths = new Set(months);
  thisMonths = thisMonths.filter((m) => knownMonths.has(m));
  lyMonths = lyMonths.filter((m) => knownMonths.has(m));

  const sumMonths = (ms: string[], outletIds?: Set<string>) =>
    ms.reduce((s, m) => s + sumByMonth(byOutletMonth, m, outletIds), 0);

  const brandThis = sumMonths(thisMonths);
  const brandLY = sumMonths(lyMonths);
  const brandPct = brandLY > 0 ? ((brandThis - brandLY) / brandLY) * 100 : null;

  // Same-store split — outlets that had any sales in BOTH the this-year and
  // LY windows. New outlets = had sales this year but not LY.
  const thisActive = new Set<string>();
  const lyActive = new Set<string>();
  for (const r of byOutletMonth) {
    if (thisMonths.includes(r.month) && r.netSales > 0) thisActive.add(r.outletId);
    if (lyMonths.includes(r.month) && r.netSales > 0) lyActive.add(r.outletId);
  }
  const sameStoreIds = new Set([...thisActive].filter((id) => lyActive.has(id)));
  const newStoreIds = new Set([...thisActive].filter((id) => !lyActive.has(id)));
  const sameStoreThis = sumMonths(thisMonths, sameStoreIds);
  const sameStoreLY = sumMonths(lyMonths, sameStoreIds);
  const sameStorePct = sameStoreLY > 0 ? ((sameStoreThis - sameStoreLY) / sameStoreLY) * 100 : null;
  const newStoreThis = sumMonths(thisMonths, newStoreIds);

  // City rollup
  const byCity = new Map<string, Outlet[]>();
  for (const o of outlets) {
    const city = o.city || 'Unknown';
    if (!byCity.has(city)) byCity.set(city, []);
    byCity.get(city)!.push(o);
  }
  const cityRows = Array.from(byCity.entries())
    .map(([city, cityOutlets]) => {
      const ids = new Set(cityOutlets.map((o) => o.id));
      const t = sumMonths(thisMonths, ids);
      const ly = sumMonths(lyMonths, ids);
      const pct = ly > 0 ? ((t - ly) / ly) * 100 : null;
      return { city, latest: t, prev: ly, pct, outletCount: cityOutlets.length };
    })
    .sort((a, b) => b.latest - a.latest);

  // Outlet rows
  const outletRows = outlets
    .map((o) => {
      const ids = new Set([o.id]);
      const t = sumMonths(thisMonths, ids);
      const ly = sumMonths(lyMonths, ids);
      const pct = ly > 0 ? ((t - ly) / ly) * 100 : null;
      const isNew = ly === 0 && t > 0;
      const line = months.slice(0, -1).map((m) => sumByMonth(byOutletMonth, m, ids));
      return { outlet: o, latest: t, prev: ly, pct, isNew, line };
    })
    .filter((r) => r.latest > 0 || r.prev > 0)
    .sort((a, b) => {
      if (a.isNew && !b.isNew) return -1;
      if (!a.isNew && b.isNew) return 1;
      const aPct = a.pct ?? -999;
      const bPct = b.pct ?? -999;
      return bPct - aPct;
    });

  const periodLabel =
    period === 'last_month'
      ? `${pretty(thisMonths[0] ?? '')} vs ${pretty(lyMonths[0] ?? '')}`
      : period === 'last_quarter'
        ? `Quarter ending ${pretty(thisMonths[thisMonths.length - 1] ?? '')}`
        : `YTD ${lastClosedY} vs ${lastClosedY - 1}`;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <YoYPeriodPicker value={period} onChange={setPeriod} />
        <span className="text-2xs text-muted-foreground">{periodLabel}</span>
      </div>

      <BrandHeroYoY
        thisLabel={periodLabel}
        latest={brandThis}
        ly={brandLY}
        pct={brandPct}
        sameStoreThis={sameStoreThis}
        sameStoreLY={sameStoreLY}
        sameStorePct={sameStorePct}
        newStoreThis={newStoreThis}
        newStoreCount={newStoreIds.size}
        sameStoreCount={sameStoreIds.size}
      />

      <Section title="Cities">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {cityRows.map((c) => (
            <CityCard key={c.city} {...c} latestMonth="" prevMonth="" yoy />
          ))}
        </div>
      </Section>

      <Section title="Outlets" trailing={<span className="text-2xs text-muted-foreground">Sorted by YoY growth · new outlets pinned top</span>}>
        <OutletMatrix
          rows={outletRows}
          months={months.slice(0, -1)}
          latestLabel="This"
          prevLabel="LY"
          yoy
        />
      </Section>
    </motion.div>
  );
}

function YoYPeriodPicker({ value, onChange }: { value: YoyPeriod; onChange: (v: YoyPeriod) => void }) {
  const opts: Array<{ k: YoyPeriod; l: string }> = [
    { k: 'last_month', l: 'Last month' },
    { k: 'last_quarter', l: 'Last quarter' },
    { k: 'ytd', l: 'YTD' },
  ];
  return (
    <div className="inline-flex rounded-md border border-border/60 bg-card p-0.5 text-xs">
      {opts.map((o) => (
        <button
          key={o.k}
          onClick={() => onChange(o.k)}
          className={cn(
            'rounded px-2.5 py-1 font-medium transition',
            value === o.k ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

// ───────── Brand heros ─────────

function BrandHeroMoM({
  latestMonth,
  prevMonth,
  latest,
  prev,
  pct,
  line,
}: {
  latestMonth: string;
  prevMonth: string;
  latest: number;
  prev: number;
  pct: number | null;
  line: Array<{ month: string; value: number }>;
}) {
  const tone = growthTone(pct);
  return (
    <Card className="overflow-hidden border-border/60 bg-gradient-to-br from-emerald-500/[0.04] via-card to-card">
      <CardContent className="grid gap-4 p-5 md:grid-cols-[1fr_auto] md:p-6">
        <div className="space-y-3">
          <div className="text-2xs uppercase tracking-wide text-muted-foreground">
            Brand · MoM · {pretty(latestMonth)} vs {pretty(prevMonth)}
          </div>
          <div className="flex flex-wrap items-baseline gap-3">
            <span className={cn('flex items-center gap-1.5 text-4xl font-semibold tabular-nums md:text-5xl', growthClasses(tone))}>
              <ArrowIcon tone={tone} />
              {pct === null ? '—' : `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`}
            </span>
            <span className="text-sm text-muted-foreground tabular-nums">
              {formatINRCompact(latest - prev)} change · {formatINRCompact(latest)} from {formatINRCompact(prev)}
            </span>
          </div>
        </div>
        <div className="min-w-[280px]">
          <div className="text-2xs uppercase tracking-wide text-muted-foreground">Trailing 13 months</div>
          <MonthLine data={line} />
        </div>
      </CardContent>
    </Card>
  );
}

function BrandHeroYoY({
  thisLabel,
  latest,
  ly,
  pct,
  sameStoreThis,
  sameStoreLY,
  sameStorePct,
  newStoreThis,
  newStoreCount,
  sameStoreCount,
}: {
  thisLabel: string;
  latest: number;
  ly: number;
  pct: number | null;
  sameStoreThis: number;
  sameStoreLY: number;
  sameStorePct: number | null;
  newStoreThis: number;
  newStoreCount: number;
  sameStoreCount: number;
}) {
  const tone = growthTone(pct);
  const sameStoreTone = growthTone(sameStorePct);
  return (
    <Card className="overflow-hidden border-border/60 bg-gradient-to-br from-emerald-500/[0.04] via-card to-card">
      <CardContent className="space-y-4 p-5 md:p-6">
        <div className="space-y-1">
          <div className="text-2xs uppercase tracking-wide text-muted-foreground">
            Brand · YoY · {thisLabel}
          </div>
          <div className="flex flex-wrap items-baseline gap-3">
            <span className={cn('flex items-center gap-1.5 text-4xl font-semibold tabular-nums md:text-5xl', growthClasses(tone))}>
              <ArrowIcon tone={tone} />
              {pct === null ? '—' : `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`}
            </span>
            <span className="text-sm text-muted-foreground tabular-nums">
              {formatINRCompact(latest - ly)} change · {formatINRCompact(latest)} from {formatINRCompact(ly)}
            </span>
          </div>
        </div>
        <div className="grid gap-3 rounded-md border border-border/60 bg-muted/20 p-3 md:grid-cols-2">
          <div>
            <div className="text-2xs uppercase tracking-wide text-muted-foreground">Same-store ({sameStoreCount} outlets)</div>
            <div className={cn('mt-0.5 flex items-baseline gap-2 text-xl font-semibold tabular-nums', growthClasses(sameStoreTone))}>
              <ArrowIcon tone={sameStoreTone} />
              {sameStorePct === null ? '—' : `${sameStorePct >= 0 ? '+' : ''}${sameStorePct.toFixed(1)}%`}
            </div>
            <div className="text-2xs text-muted-foreground tabular-nums">
              {formatINRCompact(sameStoreThis)} from {formatINRCompact(sameStoreLY)}
            </div>
          </div>
          <div>
            <div className="text-2xs uppercase tracking-wide text-muted-foreground">New outlets ({newStoreCount})</div>
            <div className="mt-0.5 text-xl font-semibold tabular-nums text-violet-500">
              +{formatINRCompact(newStoreThis)}
            </div>
            <div className="text-2xs text-muted-foreground">
              {newStoreCount === 0 ? 'no new outlets this period' : 'incremental from openings'}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ───────── City card ─────────

function CityCard({
  city,
  latest,
  prev,
  pct,
  outletCount,
  prevMonth,
  yoy,
}: {
  city: string;
  latest: number;
  prev: number;
  pct: number | null;
  outletCount: number;
  latestMonth: string;
  prevMonth: string;
  yoy?: boolean;
}) {
  const tone = growthTone(pct);
  return (
    <Card className="overflow-hidden border-border/60">
      <CardContent className="space-y-2 p-4">
        <div className="flex items-center justify-between">
          <div className="text-2xs uppercase tracking-wide text-muted-foreground">{city}</div>
          <div className="text-2xs text-muted-foreground">{outletCount} outlets</div>
        </div>
        <div className={cn('flex items-center gap-1.5 text-2xl font-semibold tabular-nums', growthClasses(tone))}>
          <ArrowIcon tone={tone} />
          {pct === null ? '—' : `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`}
        </div>
        <div className="text-2xs text-muted-foreground tabular-nums">
          {formatINRCompact(latest)} · {yoy ? 'LY' : pretty(prevMonth)} {formatINRCompact(prev)}
        </div>
      </CardContent>
    </Card>
  );
}

// ───────── Outlet matrix ─────────

function OutletMatrix({
  rows,
  latestLabel,
  prevLabel,
  trailingMonths,
}: {
  rows: Array<{
    outlet: Outlet;
    latest: number;
    prev: number;
    pct: number | null;
    isNew: boolean;
    line: number[];
    trailing?: number[];
  }>;
  months: string[];
  latestLabel: string;
  prevLabel: string;
  yoy?: boolean;
  /**
   * When present, renders one column per month BEFORE the (prev, latest)
   * pair so the operator can see a mini time series per outlet without
   * leaving the matrix. trailingMonths[length-2] should equal prev and
   * trailingMonths[length-1] equal latest — we slice them off to avoid
   * duplicating the dedicated columns.
   */
  trailingMonths?: string[];
}) {
  const nav = useNavigate();
  // Drop the last 2 entries (prev + latest) since they get dedicated columns.
  const olderMonths = trailingMonths ? trailingMonths.slice(0, -2) : [];
  const trailingHeaderCount = olderMonths.length;
  const minW = 700 + trailingHeaderCount * 80;
  return (
    <Card className="overflow-hidden border-border/60">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ minWidth: `${minW}px` }}>
          <thead className="text-2xs uppercase tracking-wide text-muted-foreground">
            <tr className="border-b border-border/60">
              <th className="px-4 py-2 text-left font-medium">Outlet</th>
              {olderMonths.map((m) => (
                <th key={m} className="px-3 py-2 text-right font-medium text-muted-foreground/70">
                  {pretty(m)}
                </th>
              ))}
              <th className="px-4 py-2 text-right font-medium">{prevLabel}</th>
              <th className="px-4 py-2 text-right font-medium">{latestLabel}</th>
              <th className="px-4 py-2 text-right font-medium">Growth</th>
              <th className="px-4 py-2 text-right font-medium">Δ ₹</th>
              <th className="px-4 py-2 text-left font-medium">Trend</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const tone = r.isNew ? 'na' : growthTone(r.pct);
              const olderVals = r.trailing
                ? r.trailing.slice(0, -2)
                : olderMonths.map(() => 0);
              return (
                <tr
                  key={r.outlet.id}
                  className="cursor-pointer border-b border-border/30 transition hover:bg-muted/30"
                  onClick={() => nav(`/outlets/${r.outlet.id}`)}
                >
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{r.outlet.name}</span>
                      {r.isNew && (
                        <span className="rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 dark:text-violet-300">
                          new
                        </span>
                      )}
                      <span className="text-2xs text-muted-foreground">{r.outlet.city}</span>
                    </div>
                  </td>
                  {olderVals.map((v, i) => (
                    <td key={i} className="px-3 py-2.5 text-right tabular-nums text-muted-foreground/80">
                      {v > 0 ? formatINRCompact(v) : '—'}
                    </td>
                  ))}
                  <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">
                    {r.prev === 0 ? '—' : formatINRCompact(r.prev)}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{formatINRCompact(r.latest)}</td>
                  <td className={cn('px-4 py-2.5 text-right tabular-nums font-medium', growthClasses(tone))}>
                    {r.isNew ? 'new' : r.pct === null ? '—' : `${r.pct >= 0 ? '+' : ''}${r.pct.toFixed(1)}%`}
                  </td>
                  <td className={cn('px-4 py-2.5 text-right tabular-nums', growthClasses(tone))}>
                    {r.isNew ? formatINRCompact(r.latest) : formatINRCompact(r.latest - r.prev)}
                  </td>
                  <td className="px-4 py-2.5">
                    <Sparkline values={r.line} tone={tone} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ───────── Mini bits ─────────

function Section({
  title,
  trailing,
  children,
}: {
  title: string;
  trailing?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-end justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
        {trailing}
      </div>
      {children}
    </div>
  );
}

function MonthLine({ data }: { data: Array<{ month: string; value: number }> }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...data.map((d) => d.value));
  const w = 280;
  const h = 64;
  const padX = 4;
  const usableW = w - padX * 2;
  const step = data.length > 1 ? usableW / (data.length - 1) : usableW;
  const px = (i: number) => padX + i * step;
  const py = (v: number) => h - (v / max) * (h - 6) - 3;
  const pts = data.map((d, i) => `${px(i)},${py(d.value)}`).join(' ');
  const hovered = hover !== null ? data[hover] : null;

  return (
    <div className="relative mt-1">
      <svg
        width={w}
        height={h}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * w;
          const idx = Math.max(0, Math.min(data.length - 1, Math.round((x - padX) / step)));
          setHover(idx);
        }}
      >
        <polyline
          points={pts}
          fill="none"
          stroke="hsl(160 84% 39%)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {data.map((d, i) => (
          <circle
            key={d.month}
            cx={px(i)}
            cy={py(d.value)}
            r={hover === i ? 3.5 : 2}
            fill="hsl(160 84% 39%)"
            style={{ transition: 'r 120ms ease' }}
          />
        ))}
        {hover !== null && hovered && (
          <line
            x1={px(hover)}
            x2={px(hover)}
            y1={0}
            y2={h}
            stroke="hsl(160 84% 39%)"
            strokeOpacity="0.25"
            strokeDasharray="2 3"
          />
        )}
      </svg>
      {hover !== null && hovered && (
        <div
          className="pointer-events-none absolute -top-2 z-10 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md border border-border/60 bg-card px-2 py-1 text-2xs shadow-md"
          style={{ left: `${(px(hover) / w) * 100}%` }}
        >
          <div className="font-semibold tabular-nums">{formatINRCompact(hovered.value)}</div>
          <div className="text-muted-foreground">{pretty(hovered.month)}</div>
        </div>
      )}
    </div>
  );
}

function Sparkline({ values, tone }: { values: number[]; tone: 'good' | 'warn' | 'bad' | 'na' }) {
  const max = Math.max(1, ...values);
  const w = 80;
  const h = 22;
  const step = values.length > 1 ? w / (values.length - 1) : w;
  const pts = values
    .map((v, i) => {
      const x = i * step;
      const y = h - (v / max) * (h - 4) - 2;
      return `${x},${y}`;
    })
    .join(' ');
  const stroke =
    tone === 'good' ? 'hsl(160 84% 39%)' : tone === 'bad' ? 'hsl(347 77% 50%)' : tone === 'warn' ? 'hsl(38 92% 50%)' : 'hsl(0 0% 60%)';
  return (
    <svg width={w} height={h}>
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border/40 bg-card/40 py-16">
      <Activity className="h-6 w-6 animate-pulse text-emerald-500" />
      <div className="text-sm text-muted-foreground">Pulling trailing 14 months of sales…</div>
    </div>
  );
}

// ───────── Discount / Ads vs Sales view ─────────

function SpendVsSalesView({
  kind,
  months,
  byOutletMonth,
  metaAdsByMonth,
  outlets,
}: {
  kind: 'discount' | 'ads';
  months: string[];
  byOutletMonth: OutletMonth[];
  metaAdsByMonth: Map<string, number>;
  outlets: Outlet[];
}) {
  // Use the 13 closed months (drop the current partial). Spend lines are
  // misleading with a half-month bucket on the right.
  const closedMonths = months.slice(0, -1);

  const salesByMonth = (m: string, ids?: Set<string>) => sumByMonth(byOutletMonth, m, ids);
  const discountByMonth = (m: string, ids?: Set<string>) => {
    let s = 0;
    for (const r of byOutletMonth) {
      if (r.month !== m) continue;
      if (ids && !ids.has(r.outletId)) continue;
      s += r.discountTotal;
    }
    return s;
  };
  const spendByMonth = (m: string) =>
    kind === 'discount' ? discountByMonth(m) : metaAdsByMonth.get(m) ?? 0;

  const series = closedMonths.map((m) => ({
    month: m,
    sales: salesByMonth(m),
    spend: spendByMonth(m),
  }));

  // Headline: most recent closed month
  const latest = series[series.length - 1];
  const prev = series[series.length - 2];
  const ratio = (s: { sales: number; spend: number }) =>
    s.sales > 0 ? (s.spend / s.sales) * 100 : 0;
  const latestRatio = latest ? ratio(latest) : 0;
  const prevRatio = prev ? ratio(prev) : 0;
  const ratioDelta = latestRatio - prevRatio;

  const totalSpend = series.reduce((s, r) => s + r.spend, 0);
  const totalSales = series.reduce((s, r) => s + r.sales, 0);
  const blendedRatio = totalSales > 0 ? (totalSpend / totalSales) * 100 : 0;

  const lower = kind === 'discount' ? 'Discount' : 'Ads';
  const lowerL = kind === 'discount' ? 'discount' : 'ads';
  // For discount the "good" direction is lower (less drag); for ads we
  // express the burn ratio as informational, no tone.

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-4"
    >
      {/* Hero: latest month spend + ratio + 13-month overlay */}
      <Card className="overflow-hidden border-border/60 bg-gradient-to-br from-emerald-500/[0.04] via-card to-card">
        <CardContent className="space-y-4 p-5 md:p-6">
          <div className="space-y-1">
            <div className="text-2xs uppercase tracking-wide text-muted-foreground">
              Brand · {lower} vs sales · trailing 13 months
            </div>
            {latest && (
              <div className="flex flex-wrap items-baseline gap-3">
                <span className="text-3xl font-semibold tabular-nums md:text-4xl">
                  {formatINRCompact(latest.spend)}
                </span>
                <span className="text-sm text-muted-foreground tabular-nums">
                  {lowerL} in {pretty(latest.month)} · {latestRatio.toFixed(1)}% of {formatINRCompact(latest.sales)} sales
                </span>
                {prev && (
                  <span className={cn('text-xs tabular-nums', ratioDelta > 0 ? 'text-rose-500' : ratioDelta < 0 ? 'text-emerald-500' : 'text-muted-foreground')}>
                    {ratioDelta > 0 ? '+' : ''}
                    {ratioDelta.toFixed(1)} pts vs {pretty(prev.month)}
                  </span>
                )}
              </div>
            )}
            <div className="text-2xs text-muted-foreground tabular-nums">
              13-month total: {formatINRCompact(totalSpend)} {lowerL} on {formatINRCompact(totalSales)} sales = {blendedRatio.toFixed(1)}% blended
            </div>
          </div>

          <SpendSalesChart series={series} kind={kind} />
        </CardContent>
      </Card>

      {/* Monthly breakdown table */}
      <Section title={`Monthly ${lowerL} vs sales`}>
        <Card className="overflow-hidden border-border/60">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="text-2xs uppercase tracking-wide text-muted-foreground">
                <tr className="border-b border-border/60">
                  <th className="px-4 py-2 text-left font-medium">Month</th>
                  <th className="px-4 py-2 text-right font-medium">Net sales</th>
                  <th className="px-4 py-2 text-right font-medium">{lower} spend</th>
                  <th className="px-4 py-2 text-right font-medium">% of sales</th>
                  <th className="px-4 py-2 text-right font-medium">MoM Δ</th>
                </tr>
              </thead>
              <tbody>
                {series
                  .slice()
                  .reverse()
                  .map((row, idx, arr) => {
                    const prevRow = arr[idx + 1];
                    const rowRatio = ratio(row);
                    const prevRowRatio = prevRow ? ratio(prevRow) : null;
                    const delta = prevRowRatio !== null ? rowRatio - prevRowRatio : null;
                    return (
                      <tr key={row.month} className="border-b border-border/30">
                        <td className="px-4 py-2.5 font-medium">{pretty(row.month)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{formatINRCompact(row.sales)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">
                          {row.spend > 0 ? formatINRCompact(row.spend) : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums">
                          {row.spend > 0 ? `${rowRatio.toFixed(1)}%` : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className={cn(
                          'px-4 py-2.5 text-right tabular-nums',
                          delta === null
                            ? 'text-muted-foreground'
                            : delta > 0
                              ? 'text-rose-500'
                              : delta < 0
                                ? 'text-emerald-500'
                                : 'text-muted-foreground',
                        )}>
                          {delta === null ? '—' : `${delta > 0 ? '+' : ''}${delta.toFixed(1)} pts`}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </Card>
      </Section>

      {/* Per-outlet (discount only — ads data isn't outlet-attributed yet) */}
      {kind === 'discount' && (
        <Section title="By outlet (last closed month)">
          <Card className="overflow-hidden border-border/60">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px] text-sm">
                <thead className="text-2xs uppercase tracking-wide text-muted-foreground">
                  <tr className="border-b border-border/60">
                    <th className="px-4 py-2 text-left font-medium">Outlet</th>
                    <th className="px-4 py-2 text-right font-medium">Net sales</th>
                    <th className="px-4 py-2 text-right font-medium">Discount</th>
                    <th className="px-4 py-2 text-right font-medium">% of sales</th>
                  </tr>
                </thead>
                <tbody>
                  {outlets
                    .map((o) => {
                      const ids = new Set([o.id]);
                      const sales = latest ? salesByMonth(latest.month, ids) : 0;
                      const disc = latest ? discountByMonth(latest.month, ids) : 0;
                      const r = sales > 0 ? (disc / sales) * 100 : 0;
                      return { outlet: o, sales, disc, ratio: r };
                    })
                    .filter((r) => r.sales > 0 || r.disc > 0)
                    .sort((a, b) => b.ratio - a.ratio)
                    .map((r) => (
                      <tr key={r.outlet.id} className="border-b border-border/30">
                        <td className="px-4 py-2.5 font-medium">{r.outlet.name}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{formatINRCompact(r.sales)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{formatINRCompact(r.disc)}</td>
                        <td className={cn(
                          'px-4 py-2.5 text-right tabular-nums font-medium',
                          r.ratio > 15 ? 'text-rose-500' : r.ratio > 10 ? 'text-amber-500' : 'text-emerald-500',
                        )}>
                          {r.ratio.toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>
        </Section>
      )}

      {kind === 'ads' && metaAdsByMonth.size === 0 && (
        <Card className="border-dashed border-border/60 bg-muted/20">
          <CardContent className="p-4 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">No Meta ads data yet.</span>{' '}
            Once SUPERMETRICS_API_KEY is set as a Supabase secret, the daily Meta ads pull
            will fill marketing.meta_ad_daily and this chart will populate retroactively.
            Aggregator ad spend (Swiggy + Zomato) lives in fooddb and isn't wired to this
            view yet — see the /aggregators Ads tab for that breakdown.
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
}

function SpendSalesChart({
  series,
  kind,
}: {
  series: Array<{ month: string; sales: number; spend: number }>;
  kind: 'discount' | 'ads';
}) {
  const [hover, setHover] = useState<number | null>(null);
  const w = 720;
  const h = 200;
  const padL = 44;
  const padR = 12;
  const padT = 14;
  const padB = 28;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const maxSales = Math.max(1, ...series.map((d) => d.sales));
  const maxSpend = Math.max(1, ...series.map((d) => d.spend));
  // Bars are spend (scaled to its own max so they're visible even when
  // discount is a small fraction of sales). Line is sales (right axis,
  // implicit). Operator reads spend by bar height, sales by line shape.
  const step = series.length > 1 ? innerW / (series.length - 1) : innerW;
  const px = (i: number) => padL + i * step;
  const py = (v: number, max: number) => padT + innerH - (v / max) * innerH;
  const barW = Math.max(8, step * 0.55);
  const salesPts = series.map((d, i) => `${px(i)},${py(d.sales, maxSales)}`).join(' ');
  const hovered = hover !== null ? series[hover] : null;
  const spendColor = kind === 'discount' ? 'hsl(347 77% 50%)' : 'hsl(258 89% 66%)';

  return (
    <div className="relative">
      <svg
        width="100%"
        viewBox={`0 0 ${w} ${h}`}
        className="block"
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * w;
          const idx = Math.max(0, Math.min(series.length - 1, Math.round((x - padL) / step)));
          setHover(idx);
        }}
      >
        {/* Gridlines */}
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <line
            key={f}
            x1={padL}
            x2={padL + innerW}
            y1={padT + innerH - innerH * f}
            y2={padT + innerH - innerH * f}
            stroke="currentColor"
            strokeOpacity="0.08"
            strokeDasharray="2 3"
          />
        ))}
        {/* Spend bars */}
        {series.map((d, i) => {
          const y = py(d.spend, maxSpend);
          return (
            <rect
              key={d.month}
              x={px(i) - barW / 2}
              y={y}
              width={barW}
              height={padT + innerH - y}
              fill={spendColor}
              opacity={hover === null || hover === i ? 0.75 : 0.4}
              rx={2}
              style={{ transition: 'opacity 120ms ease' }}
            />
          );
        })}
        {/* Sales line */}
        <polyline
          points={salesPts}
          fill="none"
          stroke="hsl(160 84% 39%)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {series.map((d, i) => (
          <circle
            key={d.month}
            cx={px(i)}
            cy={py(d.sales, maxSales)}
            r={hover === i ? 4 : 2.5}
            fill="hsl(160 84% 39%)"
            style={{ transition: 'r 120ms ease' }}
          />
        ))}
        {/* X labels — show every other month to avoid crowding */}
        {series.map((d, i) =>
          i % 2 === 0 || i === series.length - 1 ? (
            <text
              key={d.month}
              x={px(i)}
              y={h - 8}
              textAnchor="middle"
              className="fill-muted-foreground text-[10px]"
            >
              {pretty(d.month)}
            </text>
          ) : null,
        )}
        {/* Hover guide */}
        {hover !== null && (
          <line
            x1={px(hover)}
            x2={px(hover)}
            y1={padT}
            y2={padT + innerH}
            stroke="currentColor"
            strokeOpacity="0.2"
            strokeDasharray="2 3"
          />
        )}
      </svg>
      {hover !== null && hovered && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 rounded-md border border-border/60 bg-card px-2.5 py-1.5 text-2xs shadow-md"
          style={{ left: `${(px(hover) / w) * 100}%`, top: `${(padT / h) * 100}%` }}
        >
          <div className="font-medium text-muted-foreground">{pretty(hovered.month)}</div>
          <div className="mt-0.5 flex items-center gap-2 tabular-nums">
            <span className="inline-block h-2 w-2 rounded-sm bg-emerald-500" />
            {formatINRCompact(hovered.sales)} sales
          </div>
          <div className="mt-0.5 flex items-center gap-2 tabular-nums">
            <span className="inline-block h-2 w-2 rounded-sm" style={{ background: spendColor }} />
            {formatINRCompact(hovered.spend)} {kind}
          </div>
          {hovered.sales > 0 && (
            <div className="mt-1 font-semibold tabular-nums">
              {((hovered.spend / hovered.sales) * 100).toFixed(1)}% of sales
            </div>
          )}
        </div>
      )}
      <div className="mt-2 flex items-center gap-3 text-2xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-3 rounded-sm bg-emerald-500" /> Net sales
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-3 rounded-sm" style={{ background: spendColor }} />{' '}
          {kind === 'discount' ? 'Discount' : 'Ads spend'}
        </span>
      </div>
    </div>
  );
}

// ───────── Per-outlet growth story (used by OutletDetailPage) ─────────

/**
 * Embeddable growth story for a single outlet. Shows MoM headline + YoY
 * headline side-by-side, plus a 13-month sparkline. Used inside the
 * outlet detail page so every outlet gets its own growth story without
 * the operator having to leave for /growth.
 */
export function OutletGrowthStory({ outletId }: { outletId: string }) {
  const { months, byOutletMonth, isLoading } = useGrowthData();
  if (isLoading) {
    return (
      <Card className="border-border/60">
        <CardContent className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
          <Activity className="h-4 w-4 animate-pulse text-emerald-500" />
          Loading growth story…
        </CardContent>
      </Card>
    );
  }
  const ids = new Set([outletId]);
  const closedMonths = months.slice(0, -1);
  const latest = closedMonths[closedMonths.length - 1] ?? '';
  const prev = closedMonths[closedMonths.length - 2] ?? '';
  const latestSales = sumByMonth(byOutletMonth, latest, ids);
  const prevSales = sumByMonth(byOutletMonth, prev, ids);
  const momPct = prevSales > 0 ? ((latestSales - prevSales) / prevSales) * 100 : null;

  // YoY: same calendar month last year
  const [ly, lm] = latest.split('-').map(Number);
  const lyMonth = ly && lm ? `${ly - 1}-${String(lm).padStart(2, '0')}` : '';
  const lySales = lyMonth ? sumByMonth(byOutletMonth, lyMonth, ids) : 0;
  const yoyPct = lySales > 0 ? ((latestSales - lySales) / lySales) * 100 : null;

  const line = closedMonths.map((m) => ({ month: m, value: sumByMonth(byOutletMonth, m, ids) }));
  const momTone = growthTone(momPct);
  const yoyTone = growthTone(yoyPct);

  return (
    <Card className="overflow-hidden border-border/60 bg-gradient-to-br from-emerald-500/[0.03] via-card to-card">
      <CardContent className="grid gap-4 p-5 md:grid-cols-[auto_auto_1fr] md:items-end md:p-6">
        <div>
          <div className="text-2xs uppercase tracking-wide text-muted-foreground">
            MoM · {pretty(latest)} vs {pretty(prev)}
          </div>
          <div className={cn('mt-1 flex items-center gap-1.5 text-3xl font-semibold tabular-nums', growthClasses(momTone))}>
            <ArrowIcon tone={momTone} />
            {momPct === null ? '—' : `${momPct >= 0 ? '+' : ''}${momPct.toFixed(1)}%`}
          </div>
          <div className="text-2xs text-muted-foreground tabular-nums">
            {formatINRCompact(latestSales)} from {formatINRCompact(prevSales)}
          </div>
        </div>
        <div>
          <div className="text-2xs uppercase tracking-wide text-muted-foreground">
            YoY · {pretty(latest)} vs {pretty(lyMonth)}
          </div>
          <div className={cn('mt-1 flex items-center gap-1.5 text-3xl font-semibold tabular-nums', growthClasses(yoyTone))}>
            <ArrowIcon tone={yoyTone} />
            {yoyPct === null ? '—' : `${yoyPct >= 0 ? '+' : ''}${yoyPct.toFixed(1)}%`}
          </div>
          <div className="text-2xs text-muted-foreground tabular-nums">
            {lySales > 0 ? `LY ${formatINRCompact(lySales)}` : 'no LY baseline'}
          </div>
        </div>
        <div className="md:justify-self-end">
          <div className="text-2xs uppercase tracking-wide text-muted-foreground">Trailing 13 months</div>
          <MonthLine data={line} />
        </div>
      </CardContent>
    </Card>
  );
}
